import initializeChaptersForCatalog from "./updateEvaluationForCatalog";
import { PRODUCT_UI_STATE_SELECTED_EXISTING } from "../../../utlities/core.constants";
import inSiteAcrAddedOn, { inSiteAcrUpdatedOn } from "../../../pages/Product/utils/inSiteAcrAddedOn";

/** Calendar date (YYYY-MM-DD) from an API timestamp, or "" when the report has no real date. */
function calendarDate(value) {
  if (typeof value !== "string") return "";
  const match = value.match(/^\d{4}-\d{2}-\d{2}/);
  return match ? match[0] : "";
}

/**
 * Find catalog chapter id that contains the given criterion id.
 */
function findChapterIdForCriterion(editorCatalog, criterionId) {
  const critKey = String(criterionId);
  const chapters = editorCatalog?.chapters ?? [];
  const chapter = chapters.find((ch) =>
    (ch.criteria ?? []).some((c) => String(c.id) === critKey)
  );
  return chapter?.id ?? null;
}

/**
 * Match component name from API to evaluation component (case-insensitive).
 */
function findComponentIndex(components, apiName) {
  if (!apiName || !Array.isArray(components)) return -1;
  const target = String(apiName).trim();
  const idx = components.findIndex((c) => c.name === target);
  if (idx >= 0) return idx;
  const lower = target.toLowerCase();
  return components.findIndex((c) => String(c.name ?? "").toLowerCase() === lower);
}

/**
 * Merge report API responses into evaluation.chapters (after initializeChaptersForCatalog).
 * Each API row has responses[].notes for that criterion/component — that maps to adherence.notes
 * (not evaluation.notes). Null notes become "" on the component.
 */
export function mergeReportResponsesIntoEvaluation(evaluation, editorCatalog, responses) {
  if (!responses?.length) return evaluation;
  const next = { ...evaluation, chapters: { ...(evaluation.chapters || {}) } };

  responses.forEach((r) => {
    if (r?.component == null || String(r.component).trim() === "") return;
    const chapterId = findChapterIdForCriterion(editorCatalog, r.criterionId);
    if (!chapterId || !next.chapters[chapterId]) return;
    const critId = String(r.criterionId);
    const chapterBlock = { ...next.chapters[chapterId] };
    const criteria = [...(chapterBlock.criteria ?? [])];
    const critIndex = criteria.findIndex((c) => String(c.num) === critId);
    if (critIndex === -1) return;
    const crit = { ...criteria[critIndex] };
    const comps = [...(crit.components ?? [])];
    const compIdx = findComponentIndex(comps, r.component);
    if (compIdx === -1) return;
    comps[compIdx] = {
      ...comps[compIdx],
      adherence: {
        level: r.conformanceLevel != null ? String(r.conformanceLevel) : "",
        notes: r.notes != null ? String(r.notes) : "",
      },
    };
    crit.components = comps;
    criteria[critIndex] = crit;
    chapterBlock.criteria = criteria;
    next.chapters = { ...next.chapters, [chapterId]: chapterBlock };
  });

  return next;
}

/**
 * Map product API payload to evaluation.product shape.
 */
export function mapProductFromApi(productApi) {
  const p = productApi || {};
  return {
    id: p.id ?? undefined,
    name: p.name ?? "",
    version: p.version ?? "",
    description: p.description ?? "",
    category: p.category ?? "",
    isPublished: p.isPublished,
  };
}

/**
 * Map vendor API + report vendorName to evaluation.vendor shape.
 */
export function mapVendorFromApiAndReport(vendorApi, reportVendorName) {
  const v = vendorApi && typeof vendorApi === "object" ? vendorApi : {};
  const company = v.name ?? reportVendorName ?? "";
  const poc = v.primaryPoc ?? {};
  return {
    name: poc.name ?? reportVendorName ?? "",
    company_name: company,
    address:
      [v.streetAddress, v.city, v.state, v.zipCode].filter(Boolean).join(", ") ||
      v.address ||
      "",
    email: poc.email ?? v.email ?? "",
    phone: poc.phoneNumber ?? v.phone ?? "",
    website: v.website ?? "",
  };
}

/**
 * Build evaluation state from an existing report + loaded catalog + product/vendor APIs.
 */
export function buildEvaluationFromExistingReport({
  previousEvaluation,
  report,
  productApi,
  vendorApi,
  editorCatalog,
}) {
  if (report == null || typeof report !== "object") {
    throw new Error("buildEvaluationFromExistingReport requires a non-null report object.");
  }
  const {
    id: reportId,
    catalogId,
    version,
    vendorName,
    responses,
    author: reportAuthor,
    reportDetails,
    vendorContact,
  } = report;
  if (reportDetails == null || typeof reportDetails !== "object") {
    throw new Error("buildEvaluationFromExistingReport requires report.reportDetails.");
  }
  const product = mapProductFromApi(productApi);
  const vendorFromApi = mapVendorFromApiAndReport(vendorApi ?? {}, vendorName);
  const details = reportDetails;
  const relatedAcrs = Array.isArray(details.relatedAcrs)
    ? details.relatedAcrs
      .map((url) => String(url ?? "").trim())
      .filter(Boolean)
      .map((url) => ({ url, type: "primary" }))
    : [];
  const author = {
    name: reportAuthor?.name ?? "",
    company_name: reportAuthor?.companyName ?? "",
    address: reportAuthor?.address ?? "",
    email: reportAuthor?.email ?? "",
    phone: reportAuthor?.phone ?? "",
    website: reportAuthor?.websiteUrl ?? "",
  };
  const vendor = {
    ...vendorFromApi,
    name: vendorContact?.name ?? vendorFromApi.name ?? "",
    email: vendorContact?.email ?? vendorFromApi.email ?? "",
    phone: vendorContact?.phone ?? vendorFromApi.phone ?? "",
    website: vendorContact?.websiteUrl ?? vendorFromApi.website ?? "",
  };
  const notes = details.notes != null ? String(details.notes) : "";

  const base = {
    ...previousEvaluation,
    report_id: reportId,
    catalog: catalogId,
    version: String(details.version ?? version ?? "1"),
    product,
    author,
    vendor,
    product_ui_state: PRODUCT_UI_STATE_SELECTED_EXISTING,
    show_product_validation_errors: false,
    product_submit_error: undefined,
    report_save_status: "idle",
    report_save_error: undefined,
    notes,
    evaluation_methods_used: details.evaluationMethods != null
      ? String(details.evaluationMethods)
      : "",
    legal_disclaimer: details.legalDisclaimer != null
      ? String(details.legalDisclaimer)
      : "",
    repository: details.repositoryUrl != null ? String(details.repositoryUrl) : "",
    feedback: details.feedbackUrl != null ? String(details.feedbackUrl) : "",
    license: details.license != null ? String(details.license) : "",
    related_acrs: relatedAcrs,
    // The saved report has no separate report-date field. Use the publish date, then the
    // created date. Do not stamp today: that made every re-export look newly dated.
    report_date: calendarDate(inSiteAcrAddedOn(report)),
    last_modified_date: calendarDate(inSiteAcrUpdatedOn(report))
      || previousEvaluation?.last_modified_date
      || "",
    chapters: {},
  };

  const initialized = initializeChaptersForCatalog(editorCatalog, base);
  return mergeReportResponsesIntoEvaluation(initialized, editorCatalog, responses);
}
